import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import { useUserIdOrAnonId } from '@/features/account/services/User/hooks'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { fetchGraphql } from '@/features/shared/utils/dataFetching/reactQuery/graphql'
import { type StoreParams, useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { FeaturedPromotionByCategoryDocument } from '@/gql/graphql'
import {
  type QueryClient,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
} from '@tanstack/react-query'

const FeaturedPromotionByCategoryDataQuery = 'FeaturedPromotionByCategoryData'

// invalidates featured promotion progress after a cart change. queries that
// resolved to no active promotion are skipped, since there is no progress UI to
// keep in sync for them.
export const invalidateFeaturedPromotionQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    queryKey: [FeaturedPromotionByCategoryDataQuery],
    predicate: (query) => query.state.data !== null,
  })

export type FeaturedPromotionByCategoryDataQueryKey = readonly [
  typeof FeaturedPromotionByCategoryDataQuery,
  {
    storeParams: StoreParams
    categoryId: number
    customerId: number | string | undefined
    state: string
  },
]

export const featuredPromotionByCategoryDataQueryOptions = ({
  storeParams,
  categoryId,
  customerId,
  state,
}: {
  storeParams: StoreParams
  categoryId: number
  customerId: number | string | undefined
  state: string
}) =>
  queryOptions({
    queryKey: [
      FeaturedPromotionByCategoryDataQuery,
      { storeParams, categoryId, customerId, state },
    ] as const,
    queryFn: fetchFeaturedPromotionByCategoryData,
  })

async function fetchFeaturedPromotionByCategoryData(
  context: QueryFunctionContext<FeaturedPromotionByCategoryDataQueryKey>,
) {
  const [, { categoryId, customerId, state, storeParams }] = context.queryKey
  if (!storeParams.store_location_id || !customerId) return null
  const data = await fetchGraphql(
    FeaturedPromotionByCategoryDocument,
    {
      categoryId: categoryId.toString(),
      input: {
        state,
        // ideally this is optional and we follow existing patterns where services
        // look at x-user-id and x-anonymous-id headers
        customerId: customerId.toString(),
        retailerData: {
          storeLocationId: storeParams.store_location_id.toString(),
          retailerId: storeParams.store_id?.toString() ?? '',
          metroId: storeParams.metro_id?.toString() ?? '',
        },
      },
    },
    { context, authOptional: true },
  )
  const activePromotion = data.cart.promotionSummary?.promotionProgress?.[0]
  if (!activePromotion) return null

  return {
    progressHeader: activePromotion.progressHeader,
    disclaimerHeader: activePromotion.disclaimerHeader,
    disclaimerBody: activePromotion.disclaimerBody,
    // progress is a decimal value, need to convert to percentage
    progress: activePromotion.progress * 100,
  }
}

export function useQueryFeaturedPromotionByCategory(categoryId: number) {
  const storeParams = useStoreParams()
  const state = useGlobalDefaultShoppingAddress().state ?? ''
  const customerId = useUserIdOrAnonId()
  const { isFullyAuthenticated, userId } = useSession()

  return useQuery({
    ...featuredPromotionByCategoryDataQueryOptions({
      customerId,
      categoryId,
      state,
      storeParams,
    }),
    enabled:
      // if guest user OR logged in user, AND store context is available
      (!isFullyAuthenticated || Boolean(userId)) && Boolean(storeParams.store_location_id),
    // always get latest BE state when user lands on page, in the event user navigates to FP, navs away, ATCs a product on said page, then navigates back.
    // this prevents stale UI state without having to leverage invalidateCache for this specific query in ATC mutations
    refetchOnMount: 'always',
  })
}
